/**
 * Notification routing resolver.
 *
 * Maps a notification (in-app list item or FCM payload) to a target URL based on
 * `feature_key` / `notification_type` and the IDs available in `data`.
 *
 * Used by:
 *  - In-app popover click handler (MainLayout.jsx)
 *  - In-app notification list page click handler (views/MainApp/Notifications)
 *  - Foreground FCM handler (src/index.js) to inject `__sm_target_url`
 *
 * NOTE: A mirror of this mapping exists inline in `public/firebase-messaging-sw.js`
 * because the service worker cannot import from `src/`. Keep both in sync when
 * adding/changing entries.
 */

const FALLBACK_ROUTE = '/notifikasi';

const INFO_PAYMENT_FEATURE_KEYS = new Set(['info_payment']);

const TAGIHAN_FEATURE_KEYS = new Set([
  'create_billing',
  'new_billing',
  'duedate_billing',
  'over_duedate_billing',
  'success_payment',
  'pending_payment',
  'expired_payment',
  'reminder_payment',
]);

const ATTENDANCE_DAILY_KEYS = new Set(['attendance_in', 'attendance_out', 'monthly_attendance_summary']);

const ATTENDANCE_MAILBOX_KEYS = new Set([
  'attendance_permit_new',
  'attendance_permit_approved',
  'attendance_permit_rejected',
]);

function readStudentPermitId(data) {
  if (!data || typeof data !== 'object') return '';
  return pickFirstString(data.student_permit_id, data.studentPermitId);
}

function pickFirstString(...candidates) {
  for (let i = 0; i < candidates.length; i += 1) {
    const v = candidates[i];
    if (v != null) {
      const s = String(v).trim();
      if (s) return s;
    }
  }
  return '';
}

function readSchoolBillingId(data) {
  if (!data || typeof data !== 'object') return '';
  const direct = pickFirstString(data.school_billing_id, data.schoolBillingId);
  if (direct) return direct;
  if (Array.isArray(data.school_billing) && data.school_billing.length > 0) {
    const first = data.school_billing[0] || {};
    return pickFirstString(first.school_billing_id, first.schoolBillingId);
  }
  return '';
}

function readSchoolBillingUserId(data) {
  if (!data || typeof data !== 'object') return '';
  const direct = pickFirstString(data.school_billing_user_id, data.schoolBillingUserId);
  if (direct) return direct;
  if (Array.isArray(data.school_billing) && data.school_billing.length > 0) {
    const first = data.school_billing[0] || {};
    return pickFirstString(first.school_billing_user_id, first.schoolBillingUserId);
  }
  return '';
}

function readSchoolBillingInvoiceId(data) {
  if (!data || typeof data !== 'object') return '';
  return pickFirstString(data.school_billing_invoice_id, data.schoolBillingInvoiceId);
}

function normalizeInput(input) {
  if (!input || typeof input !== 'object') {
    return { featureKey: '', notificationType: '', data: {} };
  }
  // BE in-app list returns `data` as a stringified JSON. Parse so id readers
  // (school_billing_user_id, student_permit_id, etc.) can find their fields.
  let data = {};
  if (input.data && typeof input.data === 'object') {
    data = input.data;
  } else if (typeof input.data === 'string' && input.data.length > 0) {
    try {
      const parsed = JSON.parse(input.data);
      if (parsed && typeof parsed === 'object') data = parsed;
    } catch {
      data = {};
    }
  }
  // BE sometimes wraps feature_key in extra quotes: '"create_billing"' → strip them.
  const featureKey = pickFirstString(input.feature_key, input.featureKey, data.feature_key, data.featureKey).replace(
    /^"|"$/g,
    ''
  );
  const notificationType = pickFirstString(
    input.notification_type,
    input.notificationType,
    data.notification_type,
    data.notificationType
  );
  return { featureKey, notificationType, data };
}

/**
 * Resolve target route for a notification.
 *
 * @param {object} input - In-app notification entry or FCM payload.
 *   Accepts both shapes:
 *     { feature_key, notification_type, data: { ... } }   // in-app list
 *     { feature_key, notification_type, ...flatData }     // flat FCM
 * @returns {string} target route, or FALLBACK_ROUTE if not mapped.
 */
export function getNotificationTargetRoute(input) {
  const { featureKey, notificationType, data } = normalizeInput(input);

  if (INFO_PAYMENT_FEATURE_KEYS.has(featureKey)) {
    // BNS-2845: monthly info payment → land on /tagihan-ortu, tab "Tagihan Aktif" (key "1").
    return '/tagihan-ortu?tab=1';
  }

  // BNS-2846: notification_type=billing → land on /tagihan-ortu Tagihan Aktif tab
  // and auto-open the Detail Tagihan popup via ?billing_id={school_billing_user_id}.
  // Covers all tagihan feature_keys (new_billing, duedate_billing, etc.) because
  // they all carry notification_type=billing.
  if (notificationType === 'billing') {
    const billingUserId = readSchoolBillingUserId(data);
    const billingName = pickFirstString(data && data.billing_name, data && data.billingName);
    if (billingUserId) {
      let url = `/tagihan-ortu?tab=1&billing_id=${encodeURIComponent(billingUserId)}`;
      if (billingName) url += `&billing_name=${encodeURIComponent(billingName)}`;
      return url;
    }
    return '/tagihan-ortu?tab=1';
  }

  // BNS-2854: notification_type=transaction → /tagihan-ortu/detail/{school_billing_invoice_id}
  // (invoice/transaction detail page, NOT the Tagihan Aktif popup).
  if (notificationType === 'transaction') {
    const invoiceId = readSchoolBillingInvoiceId(data);
    if (invoiceId) return `/tagihan-ortu/detail/${invoiceId}`;
    return '/tagihan-ortu?tab=2';
  }

  // Legacy fallback for known tagihan feature_keys without notification_type set.
  if (TAGIHAN_FEATURE_KEYS.has(featureKey)) {
    const billingId = readSchoolBillingId(data);
    if (billingId) return `/tagihan-ortu/detail/${billingId}`;
    return '/tagihan-ortu';
  }

  // BNS-2856/2857: daily attendance (Absen Masuk/Keluar) and monthly summary →
  // detail list notifikasi with Presensi tab active.
  if (ATTENDANCE_DAILY_KEYS.has(featureKey)) {
    return '/notifikasi?kategori=presensi';
  }

  // BNS-2858/2859 (rev): surat izin notifications (approved/rejected/new) →
  // land on the mailbox list ONLY. Do NOT auto-open the detail modal — QA
  // reported the extra modal step is unwanted; the parent should just see the
  // list and pick the row themselves.
  if (ATTENDANCE_MAILBOX_KEYS.has(featureKey)) {
    return '/revamp/absensi/mailbox';
  }

  // notification_type=presence with a student_permit_id — same target as the
  // explicit attendance_permit_* feature_keys (covers payloads that only set
  // notification_type without a feature_key). Mailbox list, no auto-open.
  if (notificationType === 'presence') {
    const permitId = readStudentPermitId(data);
    if (permitId) return '/revamp/absensi/mailbox';
    return '/notifikasi?kategori=presensi';
  }

  // Heuristic: monthly attendance summary often comes without feature_key but with month/attend in data.
  if (
    !featureKey &&
    data &&
    typeof data === 'object' &&
    (data.month || data.Period) &&
    ('attend' in data || 'permit' in data || 'sick' in data || 'absent' in data)
  ) {
    return '/notifikasi?kategori=presensi';
  }

  return FALLBACK_ROUTE;
}

export const NOTIFICATION_FALLBACK_ROUTE = FALLBACK_ROUTE;

/**
 * BNS-2865: extract routing-relevant fields from a foreground FCM payload.
 *
 * BE FCM `data` map shape (all values are strings on the wire):
 *   {
 *     feature_key: "new_billing",
 *     notification_type: "billing",
 *     data: "{\"id\":\"673abc...\",\"school_billing_user_id\":123,...}"
 *   }
 *
 * The notification `id` for read-on-click lives inside that stringified `data`
 * JSON — not as a sibling of `feature_key`. Parse and flatten it here so
 * routing + read handlers can treat the result as a flat map.
 *
 * Mirror of `smExtractPayloadData` in `public/firebase-messaging-sw.js`.
 * Keep both in sync.
 *
 * @param {object} payload firebase messaging payload
 * @returns {object} merged data object suitable for `getNotificationTargetRoute`
 */
function flattenNestedDataField(merged) {
  if (!merged || typeof merged !== 'object') return;

  let nested = null;
  if (typeof merged.data === 'string' && merged.data.length > 0) {
    try {
      const parsed = JSON.parse(merged.data);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        nested = parsed;
      }
    } catch {
      // keep merged.data as the original string
    }
  } else if (merged.data && typeof merged.data === 'object' && !Array.isArray(merged.data)) {
    nested = merged.data;
  }

  if (!nested) return;

  Object.keys(nested).forEach((k) => {
    if (!(k in merged)) merged[k] = nested[k];
  });
  merged.data = nested;
}

export function extractFcmPayloadData(payload) {
  if (!payload || typeof payload !== 'object') return {};
  const fromData = payload.data && typeof payload.data === 'object' ? payload.data : {};
  const fromNotif = payload.notification && typeof payload.notification === 'object' ? payload.notification : {};
  // Notification fields first; data payload takes precedence on conflicts.
  const merged = { ...fromNotif, ...fromData };
  flattenNestedDataField(merged);
  const topLevelId = pickFirstString(
    payload.id,
    payload._id,
    payload.req_id,
    payload.notification_id,
    payload.notificationId
  );
  if (topLevelId && !merged.id && !merged._id && !merged.req_id) {
    merged.id = topLevelId;
  }

  return merged;
}

/**
 * Extract backend notification id used for PUT /notifications/read.
 *
 * Primary BE shape: `id` inside the stringified FCM `data` JSON (see
 * `extractFcmPayloadData` above). Also accepts in-app list items and legacy
 * top-level aliases (`req_id`, `notification_id`).
 *
 * Mirror of `smExtractNotificationId` in `public/firebase-messaging-sw.js`.
 *
 * @param {object} input
 * @returns {string}
 */
export function extractNotificationId(input) {
  if (!input || typeof input !== 'object') return '';

  const merged =
    input.notification != null || input.from != null || input.fcmMessageId != null
      ? extractFcmPayloadData(input)
      : extractFcmPayloadData({ data: input });

  return pickFirstString(merged.id, merged._id, merged.req_id, merged.notification_id, merged.notificationId);
}
